import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { config } from "./config.js";

/**
 * Native Cognito SRP auth (amazon-cognito-identity-js), not the Hosted UI redirect flow. The
 * password field lives on this SPA's own origin so browser password managers save the
 * credential against feedbackhub.eggblox.com instead of *.amazoncognito.com. The User Pool
 * Client only needs `authFlows: { userSrp: true }` for this -- no OAuth app client config.
 */

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.clientId,
});

export type SignInResult =
  | { outcome: "success" }
  | { outcome: "newPasswordRequired"; user: CognitoUser };

/**
 * Every branch the library's callback map can dispatch to, beyond onSuccess/onFailure. If a
 * response takes one of these paths and the caller didn't handle it, the library calls this
 * function -- if that were left undefined, the resulting TypeError happens inside the SDK's own
 * async XHR callback, outside our Promise executor's call stack, so it would NEVER reject or
 * resolve our Promise and the caller would hang forever with no visible error. Rejecting here
 * with a readable message is what turns "button stuck on Setting password..." into an actual
 * error the user (and we) can see.
 */
function unexpectedChallenge(name: string, reject: (err: Error) => void) {
  return (..._args: unknown[]) => reject(new Error(`Unexpected sign-in challenge: ${name}.`));
}

/** Cognito invite users get a temp password (bootstrap-admin.ps1) that must be reset on first login. */
export function completeNewPassword(user: CognitoUser, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
      mfaRequired: unexpectedChallenge("SMS_MFA", reject),
      totpRequired: unexpectedChallenge("SOFTWARE_TOKEN_MFA", reject),
      mfaSetup: unexpectedChallenge("MFA_SETUP", reject),
      selectMFAType: unexpectedChallenge("SELECT_MFA_TYPE", reject),
      customChallenge: unexpectedChallenge("CUSTOM_CHALLENGE", reject),
      newPasswordRequired: unexpectedChallenge("NEW_PASSWORD_REQUIRED", reject),
    });
  });
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: () => resolve({ outcome: "success" }),
      onFailure: (err: Error) => reject(err),
      newPasswordRequired: () => resolve({ outcome: "newPasswordRequired", user }),
      mfaRequired: unexpectedChallenge("SMS_MFA", reject),
      totpRequired: unexpectedChallenge("SOFTWARE_TOKEN_MFA", reject),
      mfaSetup: unexpectedChallenge("MFA_SETUP", reject),
      selectMFAType: unexpectedChallenge("SELECT_MFA_TYPE", reject),
      customChallenge: unexpectedChallenge("CUSTOM_CHALLENGE", reject),
    });
  });
}

function currentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

function getSession(): Promise<CognitoUserSession | null> {
  const user = currentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      // An error here means the stored refresh token is gone/expired -- just treat it as
      // signed-out rather than surfacing it as a failure.
      resolve(!err && session && session.isValid() ? session : null);
    });
  });
}

export interface AdminUser {
  email: string;
}

export async function getUser(): Promise<AdminUser | null> {
  const session = await getSession();
  if (!session) return null;
  const email = session.getIdToken().payload.email;
  return { email: typeof email === "string" ? email : session.getIdToken().payload.sub };
}

export function signOut(): void {
  currentUser()?.signOut();
}

/** ID token — HttpJwtAuthorizer checks aud against the SPA client id. */
export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session?.getIdToken().getJwtToken() ?? null;
}
